/* eslint-disable @typescript-eslint/no-explicit-any */

import { err, ok, Result } from 'neverthrow'
import { safeParse, ValidationFail } from '../validation/core'
import { z } from 'zod'
import { Fail } from '../Fail'

export function getProp<
  Rec,
  Path extends null | (string & TStringPathsOf<Rec>),
>(record: Rec, path: Path): TGetPropAtStringPath<Rec, Path>

export function getProp<Rec, Path extends string[] & TArrayPathsOf<Rec>>(
  record: Rec,
  path: Path,
): TGetPropAtArrayPath<Rec, Path>

export function getProp(record: any, path: string[] | string): any {
  return getPropUntyped(record, path).match(
    it => it,
    err => {
      throw err
    },
  )
}

export function getPropUntyped<S extends z.ZodTypeAny = z.ZodUnknown>(
  record: unknown,
  path: null | string | string[],
  schema?: S,
): Result<z.infer<S>, Fail | ValidationFail> {
  const parseOutput = (output: unknown): Result<z.infer<S>, ValidationFail> => {
    if (schema === undefined) return ok(output)
    return safeParse(schema, output)
  }

  if (path === null || (Array.isArray(path) && path.length === 0))
    return parseOutput(record)

  const pathArray = Array.isArray(path) ? path : path.split('.')

  let value: unknown = record
  for (let i = 0; i < pathArray.length; i++) {
    const key = pathArray[i]

    if (typeof value !== 'object' || value === null) {
      return err(new Fail('attempted to access sub-property of a non-object.'))
    }

    if (key === '__proto__' || key === 'constructor' || key === 'prototype')
      return err(new Fail('attempted to access guarded key.'))

    value = value[key]
  }
  return parseOutput(value)
}

export type TStringPathsOf<T> = null | _TStringPathsOf<T>
type _TStringPathsOf<T> = T extends Record<string | number, any>
  ?
      | {
          [K in TKeyExcludes<T>]: NonNullable<T[K]> extends Record<
            string | number,
            any
          >
            ? `${K}${`.${_TStringPathsOf<T[K]>}`}` | `${K}`
            : `${K}`
        }[TKeyExcludes<T>]
  : never

export type TArrayPathsOf<T> = [] | _TArrayPathsOf<T>
type _TArrayPathsOf<T> = T extends Record<string | number, any>
  ?
      | {
          [K in TKeyExcludes<T>]: NonNullable<T[K]> extends Record<
            string | number,
            any
          >
            ? [`${K}`, ..._TArrayPathsOf<T[K]>] | [`${K}`]
            : [`${K}`]
        }[TKeyExcludes<T>]
  : never

export type TGetPropAtArrayPath<Rec, Path extends string[]> = Path extends [
  infer Key,
  ...infer Rest,
]
  ? Key extends keyof Rec
    ? Rest extends string[]
      ? TGetPropAtArrayPath<TGetProp<Rec, Key>, Rest>
      : undefined
    : undefined
  : Path extends []
  ? Rec
  : never

export type TGetPropAtStringPath<
  Rec,
  Path extends string | null,
> = Path extends `${infer Key}.${infer Rest}`
  ? Key extends keyof Rec
    ? TGetPropAtStringPath<TGetProp<Rec, Key>, Rest>
    : undefined
  : Path extends null
  ? Rec
  : never

export type TGetProp<Rec, Key> = Key extends keyof Rec
  ? Rec[Key]
  : Key extends keyof NonNullable<Rec>
  ? NonNullable<Rec>[Key] | undefined
  : undefined

type TKeyExcludes<T> = Exclude<
  keyof T,
  | symbol
  | '__proto__'
  | 'constructor'
  | 'prototype'
  | (T extends any[] ? Exclude<keyof [], number> : never)
>